/**
 * Published document → internal document. The other crossing.
 *
 * Everything arriving here is untrusted: an export file a user picked, a
 * response from a service, a browser storage value any script in the origin can
 * write. So this file does not validate-then-cast — it *rebuilds*, delegating
 * the per-field coercion to `services/parseStoredCards.ts`, which already
 * whitelists every enum, caps every string and clamps every number. Two readers
 * with two ideas of what a valid card is would be worse than one strict one.
 *
 * WHAT THIS ADDS ON TOP: version handling, and naming what it refused.
 *
 * `parseStoredCards` returns the cards it could rescue. That is the right
 * behaviour for one card in a list, and the wrong behaviour for a whole
 * document: a caller that receives three cards from a five-card file, and then
 * saves, has just deleted two. Everything refused comes back in `rejected` so a
 * caller can quarantine the bytes instead of writing over them.
 */

import { coerceCard } from '../services/parseStoredCards';
import { ProductCard } from '../types';
import { PRODUCT_CARD_SCHEMA_VERSION } from './version';
import { DecodeRejection, DecodeResult, WireProductCard } from './types';

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * The cards out of either accepted container.
 *
 * A bare array is the format this module wrote before the envelope existed, and
 * documents in it are still on people's machines. Reading both is a one-line
 * concession that avoids a migration nobody can supervise; only the envelope is
 * ever *written*.
 */
const cardsFrom = (
  document: unknown
): { cards: unknown[]; version: number } | DecodeRejection => {
  if (Array.isArray(document)) {
    // Legacy: no envelope, so no version to check. Version 1 is the only shape
    // that was ever written this way.
    return { cards: document, version: 1 };
  }

  if (!isRecord(document)) {
    return {
      index: -1,
      id: null,
      reason: 'not-a-collection',
      detail: `expected an object or an array, got ${document === null ? 'null' : typeof document}`,
    };
  }

  const version =
    typeof document.schemaVersion === 'number' && Number.isFinite(document.schemaVersion)
      ? document.schemaVersion
      : 1;

  /**
   * A newer document is refused whole, never partially read.
   *
   * The tempting alternative — "parse the properties I recognise" — assumes a
   * major bump only *adds*. It does not: the policy in version.ts reserves major
   * versions for removals, narrowings and changes of meaning. A key that still
   * parses may no longer mean what this reader thinks it means, and a document
   * silently misread is worse than one refused loudly.
   */
  if (version > PRODUCT_CARD_SCHEMA_VERSION) {
    return {
      index: -1,
      id: null,
      reason: 'unsupported-version',
      detail:
        `document is schemaVersion ${version}; this reader understands up to ` +
        `${PRODUCT_CARD_SCHEMA_VERSION}. Refusing rather than reading the parts it recognises.`,
    };
  }

  if (!Array.isArray(document.cards)) {
    return {
      index: -1,
      id: null,
      reason: 'not-a-collection',
      detail: 'no `cards` array on the envelope',
    };
  }

  return { cards: document.cards, version };
};

const isRejection = (value: unknown): value is DecodeRejection =>
  isRecord(value) && typeof value.reason === 'string';

/**
 * A parsed document → internal cards, plus everything it refused.
 *
 * `fallbackTimestamp` stands in for a card whose `updatedAt` is missing or
 * unreadable; the caller supplies it so this stays clock-free.
 */
export const decodeDocument = (
  document: unknown,
  fallbackTimestamp: string
): { cards: ProductCard[]; rejected: DecodeRejection[] } => {
  const extracted = cardsFrom(document);
  if (isRejection(extracted)) {
    return { cards: [], rejected: [extracted] };
  }

  const cards: ProductCard[] = [];
  const rejected: DecodeRejection[] = [];
  const seenIds = new Set<string>();

  extracted.cards.forEach((candidate, index) => {
    const card = coerceCard(candidate, fallbackTimestamp);
    if (!card) {
      rejected.push({
        index,
        id: isRecord(candidate) && typeof candidate.id === 'string' ? candidate.id : null,
        reason: 'unusable-card',
        detail: 'not an object, or carried no usable id',
      });
      return;
    }
    if (seenIds.has(card.id)) {
      // First writer wins. Silently keeping the last would make the outcome
      // depend on array order, which no caller controls.
      rejected.push({
        index,
        id: card.id,
        reason: 'unusable-card',
        detail: `duplicate id "${card.id}"; the first occurrence was kept`,
      });
      return;
    }
    seenIds.add(card.id);
    cards.push(card);
  });

  return { cards, rejected };
};

/** Raw bytes → internal cards. The entry point for a file or a response body. */
export const decodeCollection = (raw: string, fallbackTimestamp: string) => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    return {
      cards: [] as ProductCard[],
      rejected: [
        {
          index: -1,
          id: null,
          reason: 'not-json' as const,
          detail: error instanceof Error ? error.message : 'unparseable',
        },
      ],
    };
  }
  return decodeDocument(parsed, fallbackTimestamp);
};

/**
 * Whether a value is shaped like a published card, for a caller that wants to
 * check before decoding. A cheap structural probe, NOT validation — the schema
 * in `schema.ts` is what validates. It answers "is this the right kind of
 * thing", so a wrong-file-picked error can be told apart from a corrupt one.
 */
export const looksLikeWireCard = (value: unknown): value is WireProductCard =>
  isRecord(value) && typeof value.id === 'string' && Array.isArray(value.fieldOrder);

export type { DecodeResult };
